import React, { useState } from 'react';
import type { IconV2Props } from '@withjoy/joykit';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { Settings, Invite, NotificationBubble, Help, Guests, Profile, LogOut, ChevronDown } from '@withjoy/joykit/icons';
import { RoutesProvider, useRouterHelper, useTranslation } from '@shared/core';
import { BurgerMenu } from './components/BurgerMenu/BurgerMenu';
import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import { useEventInfo } from '@shared/utils/eventInfo';
import { MenuItem } from './components/BurgerMenu/MenuItem';
import { monorepoRoutePaths } from '@apps/admin/route.paths';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { GLOBAL_NAV_MENU, useUniversalBurgerMenuController } from './UniversalBurgerMenu.controller';
import { MenuHeader } from './MenuHeader';
import { styles } from './UniversalBurgerMenu.styles';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { EventType } from '@graphql/generated';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { useInviteAdminDialogContext } from '@shared/context/InviteAdminDialog/InviteAdminDialog.provider';
import { useIntercom } from '@shared/core/intercom';
import { animationTransition } from '@shared/utils/animationTransition';

type MenuFooterProps = {
  time?: string;
  photoUrl?: string;
  location: Maybe<string>;
  trackAndProvideLogout: () => void;
};

const MenuFooterMenuItem = (props: {
  title: string;
  onClick: () => void;
  path?: string;
  pathIsInJoyWeb?: boolean;
  icon?: React.ComponentType<IconV2Props>;
  topDivider?: boolean;
}) => {
  return (
    <MenuItem
      typographyVariant="body1"
      color="mono14"
      path={props.path}
      fontWeight={400}
      text={props.title}
      pathIsInJoyWeb={props.pathIsInJoyWeb}
      onClick={props.onClick}
      icon={props.icon}
      topDivider={props.topDivider}
    />
  );
};

const MenuFooter = ({ time, location: _location, photoUrl, trackAndProvideLogout }: MenuFooterProps) => {
  const eventInfoResult = useEventInfo();
  const eventInfo = eventInfoResult?.eventInfo;
  const { tq } = useTranslation('adminDashboardV3');
  const { buildPath } = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const inviteAdminDialogContext = useInviteAdminDialogContext(false);
  const intercom = useIntercom();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const eventName = eventInfo?.eventDisplayName ?? '';

  return (
    <Box
      position="sticky"
      bottom={0}
      zIndex={100}
      transition={animationTransition('opacity', 'transform', { duration: '0.3s', timingFunction: 'ease-in-out' })}
      marginTop="auto"
      borderTop="1px solid"
      borderTopColor="mono1"
      background="linear-gradient(180deg, rgba(247, 247, 247, 0.85) 0%, rgba(247, 247, 247, 0.85) 100%)"
      boxShadow="0 -4px 20px 0 rgba(0, 0, 0, 0.05)"
      backdropFilter="blur(10px)"
    >
      <Box
        padding={5}
        paddingLeft={5}
        cursor="pointer"
        transition={animationTransition('background-color', { duration: '0.3s', timingFunction: 'ease-in-out' })}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Flex
          columnGap={5}
          alignItems="center"
        >
          <Box __css={styles.eventWidgetPhoto(photoUrl)} />
          <Flex
            flexDirection="column"
            rowGap={pxToRem(2)}
            flex={1}
          >
            <TextV2 typographyVariant="hed2">{eventName}</TextV2>
            {time && (
              <TextV2
                typographyVariant="label2"
                color="mono12"
              >
                {time}
              </TextV2>
            )}
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="center"
            width={pxToRem(24)}
            height={pxToRem(24)}
            flexShrink={0}
          >
            {isExpanded && <ChevronDown size={20} />}
          </Flex>
        </Flex>
      </Box>
      <Box
        display="grid"
        gridTemplateRows={isExpanded ? '1fr' : '0fr'}
        overflow="hidden"
        opacity={isExpanded ? 1 : 0}
        transition={animationTransition('grid-template-rows', 'opacity', { duration: '0.3s', timingFunction: 'ease-in-out' })}
        __css={{
          '& a > li, & [role="button"] > li': {
            borderRadius: '16px',
            '&:hover': {
              backgroundColor: 'white',
              background: 'white',
            },
            '&:active': {
              backgroundColor: theme.colors.mono2,
              background: theme.colors.mono2,
              '& svg': {
                fill: theme.colors.newUIBrandPrimary,
              },
            },
          },
        }}
      >
        <Box overflow="hidden">
          <Flex
            flexDirection="column"
            paddingLeft={5}
            paddingRight={0}
            marginRight={0}
          >
            {eventInfo?.eventType !== EventType.babyRegistry && (
              <Box paddingTop={5}>
                <MenuFooterMenuItem
                  title={tq`Event Settings`}
                  icon={Settings}
                  path={buildPath(monorepoRoutePaths.settings)}
                  pathIsInJoyWeb
                  onClick={() => telemetry.openPage({ label: 'settings', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU })}
                />
              </Box>
            )}
            {inviteAdminDialogContext && (
              <MenuFooterMenuItem
                title={tq`Invite Collaborator`}
                icon={Invite}
                onClick={() => {
                  telemetry.openPage({ label: 'inviteAdmin', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU });
                  inviteAdminDialogContext.openInviteAdminDialog();
                }}
              />
            )}
            <MenuFooterMenuItem
              title={tq`Contact Support`}
              icon={NotificationBubble}
              onClick={() => {
                telemetry.openPage({ label: 'contactSupport', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU });
                intercom.show();
              }}
            />
            <MenuFooterMenuItem
              title={tq`Help`}
              icon={Help}
              path="https://withjoy.com/help"
              onClick={() => telemetry.openPage({ label: 'help', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU })}
            />
            <MenuFooterMenuItem
              title={tq`Manage Events`}
              icon={Guests}
              path="/account/events"
              pathIsInJoyWeb
              topDivider
              onClick={() => telemetry.openPage({ label: 'myEvents', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU })}
            />
            <MenuFooterMenuItem
              title={tq`Account Info`}
              icon={Profile}
              path={`/${monorepoRoutePaths.accountPersonalInfo}`}
              pathIsInJoyWeb
              onClick={() => telemetry.openPage({ label: 'account', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU })}
            />
            <Box paddingBottom={6}>
              <MenuItem
                typographyVariant="body1"
                color="mono14"
                fontWeight={500}
                text={tq`Sign Out`}
                icon={LogOut}
                topDivider
                onClick={() => {
                  telemetry.openPage({ label: 'accountSignOut', section: 'Avatar', categoryArg: GLOBAL_NAV_MENU });
                  trackAndProvideLogout();
                }}
              />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

const Root = ({
  useModernMenu,
  eventInfo,
  children,
}: {
  useModernMenu: boolean;
  eventInfo: {
    eventType: EventType;
    eventHandle: string;
    eventId: string;
  };
  children: React.ReactNode;
}) => {
  const isMobile = useIsMobileScreen();
  const { items, trackAndProvideLogout, eventDate, location, photoUrl } = useUniversalBurgerMenuController();

  return (
    <BurgerMenu
      items={items}
      enableMobileFirst={isMobile}
      DrawerHeaderContent={<MenuHeader />}
      DrawerFooterContent={
        <MenuFooter
          time={eventDate}
          location={location}
          photoUrl={photoUrl}
          trackAndProvideLogout={trackAndProvideLogout}
        />
      }
      useModernMenu={useModernMenu}
      eventInfo={eventInfo}
    >
      {children}
    </BurgerMenu>
  );
};

export const UniversalBurgerMenu: React.FC<{ eventHandle?: string }> = ({ children, eventHandle }) => {
  const eventInfo = useEventInfo();
  const useModernMenu = useProFeature(EventFeature.menu);

  const handle = eventHandle || eventInfo?.eventHandle || '';
  const basicEventInfo = {
    eventType: eventInfo?.eventInfo?.eventType || EventType.wedding,
    eventHandle: handle,
    eventId: eventInfo?.eventInfo?.eventId || '',
  };

  return (
    <RoutesProvider handle={handle}>
      <Root
        useModernMenu={useModernMenu}
        eventInfo={basicEventInfo}
      >
        {children}
      </Root>
    </RoutesProvider>
  );
};
